import { HotelNavigationUtility } from "@b2bportal/core-utilities";
import type { GuestsSelection, Suggestion } from "@b2bportal/lodging-api";
import dayjs from "dayjs";

export interface BuildCarsAvailabilityUrlParams {
  pickupDate: string;
  dropoffDate: string;
  pickupTime: string;
  dropoffTime: string;
  driverAge: string;
  pickupLocationIdentifier: string;
  dropoffLocationIdentifier?: string;
  perkspotUserId?: string;
  communityId?: number;
}

const COMMERCE_PORTAL_BASE_URL =
  import.meta.env.VITE_COMMERCE_PORTAL_BASE_URL ?? "";

/**
 * The userID coming from PerkSpot. The ID is already obfuscated and is not considered PII as per their technical team.
 */
export const PTN_PERKSPOT_USER_ID = "ptn_perkspotUserId";
/**
 * The community ID for the user, used by Commerce Portals for analytics.
 */
export const PTN_COMMUNITY_ID = "ptn_communityId";

export const buildCarsAvailabilityUrl = ({
  pickupDate,
  dropoffDate,
  pickupTime,
  dropoffTime,
  driverAge,
  pickupLocationIdentifier,
  dropoffLocationIdentifier,
  perkspotUserId,
  communityId,
}: BuildCarsAvailabilityUrlParams): string => {
  // Set locale (commerce-portals expects locale in path)
  // Note: en-US is the only locale currently supported and is unlikely to change
  const locale = "en-US";

  // Use identifiers from location objects (iataCode or googlePlaceId)
  const pickup = pickupLocationIdentifier;

  // Combine date and time into datetime strings
  const pickupDateTime = dayjs(pickupDate)
    .hour(dayjs(pickupTime).hour())
    .minute(dayjs(pickupTime).minute())
    .second(0)
    .format("YYYY-MM-DDTHH:mm:ss");

  const dropOffDateTime = dayjs(dropoffDate)
    .hour(dayjs(dropoffTime).hour())
    .minute(dayjs(dropoffTime).minute())
    .second(0)
    .format("YYYY-MM-DDTHH:mm:ss");

  const usePoints = "false";

  // Build search params manually for commerce-portals
  const searchParams = new URLSearchParams({
    pickup,
    pickupDateTime,
    dropOffDateTime,
    driverAge,
    usePoints,
  });

  // Only include dropoff when it differs from pickup; otherwise the target URL
  // would treat this as a "different return location" search.
  if (
    dropoffLocationIdentifier &&
    dropoffLocationIdentifier !== pickupLocationIdentifier
  ) {
    searchParams.set("dropoff", dropoffLocationIdentifier);
  }

  if (perkspotUserId != null) {
    searchParams.set(PTN_PERKSPOT_USER_ID, perkspotUserId);
  }
  if (communityId != null) {
    searchParams.set(PTN_COMMUNITY_ID, `${communityId}`);
  }

  return `${COMMERCE_PORTAL_BASE_URL}/${locale}/cars/availability?${searchParams.toString()}`;
};

interface TripUrlParams {
  reservationId: string;
  perkspotUserId?: string;
  communityId?: number;
}

/**
 * Prefixes a commerce-portals path with the portal base URL and appends the
 * PerkSpot attribution params that are set.
 */
const withAttribution = (
  path: string,
  { perkspotUserId, communityId }: Omit<TripUrlParams, "reservationId">
): string => {
  const searchParams = new URLSearchParams();

  if (perkspotUserId != null) {
    searchParams.set(PTN_PERKSPOT_USER_ID, perkspotUserId);
  }
  if (communityId != null) {
    searchParams.set(PTN_COMMUNITY_ID, `${communityId}`);
  }

  const query = searchParams.toString();
  const base = `${COMMERCE_PORTAL_BASE_URL}${path}`;
  return query ? `${base}?${query}` : base;
};

export const buildCarTripDetailsUrl = ({
  reservationId,
  ...attribution
}: TripUrlParams): string =>
  withAttribution(`/cars/trips/${reservationId}`, attribution);

/**
 * Commerce keys stay trip details by the lodging reservation booking id, the
 * same `reservationBookingId` the legacy hotel trips list carries (ATL-1503).
 */
export const buildStayTripDetailsUrl = ({
  reservationId,
  ...attribution
}: TripUrlParams): string =>
  withAttribution(
    `/stays/trips/${encodeURIComponent(reservationId)}`,
    attribution
  );

/**
 * Commerce's stay cancel flow, the replacement for the legacy
 * `/trips/hotel/cancel/{reservationId}` page (ATL-1503).
 */
export const buildStayTripCancelUrl = ({
  reservationId,
  ...attribution
}: TripUrlParams): string =>
  withAttribution(
    `/stays/trips_/${encodeURIComponent(reservationId)}/actions/cancel`,
    attribution
  );

const PLATFORM_SEARCH_TYPE = "hts" as const;

export const buildPlatformAvailabilityUrl = ({
  place,
  dateRange,
  guests,
  roomsCount,
  perkspotUserId,
  communityId,
}: {
  place: Suggestion;
  dateRange: { from: string; until: string };
  guests: GuestsSelection;
  roomsCount: number;
  perkspotUserId?: string;
  communityId?: number;
}): string => {
  const searchParams = new URLSearchParams();

  if (place.id.Id === "Lodgings") {
    const { lodgingSelection } = place.id;
    if (lodgingSelection.LodgingSelection === "Place") {
      searchParams.set("locationLabel", lodgingSelection.searchTerm);
      searchParams.set("location", lodgingSelection.placeId);
    } else {
      // The autocomplete API can return non-Place variants (e.g. Location) for coordinate-based
      // queries (Query.Coordinate), but the search input only uses text queries (Query.Label),
      // which always return Place. This branch should be unreachable in practice.

      // Log to monitoring for investigation
      console.error("Unexpected lodging selection type:", lodgingSelection);
      // Fall back to legacy behavior or show user-friendly error
      throw new Error("Unsupported location type");
    }
  }

  searchParams.set(
    "checkInDate",
    HotelNavigationUtility.formatDateForUrl(dateRange.from)
  );
  searchParams.set(
    "checkOutDate",
    HotelNavigationUtility.formatDateForUrl(dateRange.until)
  );
  searchParams.set("adults", `${guests.adults}`);
  searchParams.set("rooms", `${roomsCount}`);
  searchParams.set("platform", PLATFORM_SEARCH_TYPE);

  if (guests.children?.length) {
    searchParams.set("childrenAges", guests.children.join(","));
  }

  if (perkspotUserId != null) {
    searchParams.set(PTN_PERKSPOT_USER_ID, perkspotUserId);
  }
  if (communityId != null) {
    searchParams.set(PTN_COMMUNITY_ID, `${communityId}`);
  }

  return `${COMMERCE_PORTAL_BASE_URL}/stays/availability?${searchParams.toString()}`;
};
